// @ts-strict-ignore
import Highcharts, {Options, Point, TooltipFormatterCallbackFunction} from 'highcharts';
import sum from 'lodash/sum';
import {AttrsSchema, Component, jsx} from 'panel';
import ResizeObserver from 'resize-observer-polyfill';
import isEqual from 'lodash/isEqual';

import {truncateMiddle} from 'common/util/string';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {getChartSegmentColorCombo} from 'common/util/colors';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {HoveredItem} from 'common/charts/contexts/segment-filter-controller';
import {getSeriesMatchPath} from 'common/charts/contexts/utils';

import {ChartDataItem, PieChartJsxProps, TooltipFormatter, TooltipFormatterParams} from './types';
import {defaultTooltipFormatter, setPieSeriesState} from './util';
import css from './index.inline.styl';

export class PieChart extends Component<
  EmptyObj,
  PieChartJsxProps[`attrs`],
  any,
  any,
  ChartsContextRegistry,
  PieChartJsxProps[`params`]
> {
  static get attrsSchema(): AttrsSchema<PieChartJsxProps[`attrs`]> {
    return {
      ...super.attrsSchema,
      'disable-animation': `boolean`,
      elref: `string`,
    };
  }

  public get config() {
    return {
      css,
      defaultParams: {
        getMetricKeyForName: (name: string) => name,
        valueFormatter: (_metricKey: string, value: number) => value + ``,
      },
      params: {
        getMetricKeyForName: Function,
        valueFormatter: Function,
      },
      useShadowDom: true,
      template: () => jsx(`div`, {}),
      contexts: [`segmentFilterController` as const],
      hooks: {
        preUpdate: ((_newState, newParams) => {
          // Dumb workaround till we convert this to React and can have a useEffect to do
          // this only when the passed in function changes
          if (newParams?.valueFormatter && newParams.valueFormatter !== this.params.valueFormatter) {
            this.updateChart();
          }
        }) as any,
        postUpdate: () => {
          this.tableHovered(this.getContext(`segmentFilterController`)?.hoveredSeries);
        },
      },
    };
  }

  public attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === `disable-animation`) {
      this.updateChart();
    }
  }

  public connectedCallback() {
    super.connectedCallback();

    if (!this.chartData) {
      throw new Error(`"chartData" is a required component prop`);
    }

    this.selfResizeObserver = new ResizeObserver((entries) => {
      const {height} = entries[0].contentRect;
      this.chartHeight = height;
      this.updateChart();
    });
    this.selfResizeObserver.observe(this);

    const chartEl = document.createElement(`div`);
    chartEl.classList.add(`mp-highcharts-root`);
    chartEl.style.width = `100%`;
    chartEl.style.height = `100%`;
    this.el.appendChild(chartEl);

    this.highcharts = new Highcharts.Chart(chartEl, this.getChartOptions());
    this.updateChart();
  }

  public disconnectedCallback() {
    this.selfResizeObserver.disconnect();

    if (this.highcharts) {
      this.highcharts.destroy();
      delete this.highcharts;
    }
    super.disconnectedCallback();
  }

  /** Hovers a pie slice (highcharts point) when the corresponding data table row is hovered */
  tableHovered = (hovered: HoveredItem) => {
    if (this.highcharts.container.style.pointerEvents === `none`) {
      // the chart is disabled, like when a popover is open
      return;
    }

    let hoveredPoints: Point[] = null;
    if (hovered?.seriesId && hovered.partialMatch) {
      const matchPath = getSeriesMatchPath(hovered.seriesId);
      hoveredPoints = this.highcharts.series[0].points.filter((s) => getSeriesMatchPath(s.name) === matchPath);
    } else if (hovered?.seriesId) {
      const point = this.highcharts.get(hovered.seriesId) as Point;
      hoveredPoints = point ? [point] : [];
    }

    if (hoveredPoints?.length) {
      setPieSeriesState(`hover`, this.highcharts.series[0], hoveredPoints);
    } else {
      setPieSeriesState(`normal`, this.highcharts.series[0]);
    }
  };

  public declare tooltipFormatter: TooltipFormatter;
  public declare onPieChartClick: (point: Highcharts.Point) => void;

  private _chartData: ChartDataItem[] = [];
  public get chartData() {
    return this._chartData;
  }
  public set chartData(data: ChartDataItem[]) {
    if (!isEqual(data, this._chartData)) {
      this._chartData = data;
      this.updateChart();
    }
  }

  private getChartData(): ChartDataItem[] {
    this._total = sum(this.chartData.map((item) => item.value));
    return this.chartData.map((item, idx) => {
      const metricKey = this.params.getMetricKeyForName(item.name);
      return {
        ...item,
        color: item.color || getChartSegmentColorCombo(idx).primary, // assign default primary colors if there's none
        id: item.name,
        metricKey,
        name: item.name,
        percent: item.percent,
        secondaryColor: item.secondaryColor || getChartSegmentColorCombo(idx).secondary, // assign default secondary colors if there's none
        value: +item.value,
        y: item.pieChartValue || item.value,
      };
    });
  }

  private declare _total?: number;
  public get total() {
    return this._total;
  }

  // only public because tests may need to access it
  public declare highcharts: Highcharts.Chart;

  private declare selfResizeObserver: ResizeObserver;
  private declare chartHeight: number; // px

  private createTooltipFormatter(): TooltipFormatterCallbackFunction {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const component = this;
    return function () {
      const params: TooltipFormatterParams = {
        name: this.key,
        value: this.point.y,
        percent: this.point.percentage,
        color: this.point.color as string,
        point: this.point,
      };
      return component.tooltipFormatter ? component.tooltipFormatter(params) : defaultTooltipFormatter(params);
    };
  }

  private getChartOptions(): Options {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const thisComponent = this;
    const isSmall = this.chartHeight <= 300;

    return {
      // TODO consider using the accessibility.js module. This silences warning in the meantime.
      accessibility: {enabled: false},
      chart: {
        animation: false,
        backgroundColor: null,
        type: `pie`,
        style: {
          fontFamily: mpFonts.fontFamilyPrimary,
        },
        margin: isSmall ? 8 : 64,
      },
      credits: {
        enabled: false,
      },
      title: {
        align: `center`,
        style: {
          color: null,
          fill: null,
          fontSize: isSmall ? `19px` : undefined,
        },
        text: thisComponent.params.valueFormatter(``, this.total),
        verticalAlign: `middle`,
      },
      plotOptions: {
        pie: {
          ...(this.onPieChartClick && {cursor: `pointer`}),
          dataLabels: {
            style: {
              color: null,
              fill: null,
              fontSize: undefined,
              textOutline: `none`,
            },
            color: null,
            enabled: !isSmall,
            formatter(this) {
              const options: Highcharts.PointOptionsObject & ChartDataItem = this.point.options as any;
              const label = options.displayName ?? this.key;
              const metricKey = options.metricKey;
              const formatted = thisComponent.params.valueFormatter(metricKey, this.y);
              return `
                <div class="pie-segment-label">
                  ${truncateMiddle(label, 30)}
                  <br />
                  <div class="value">${formatted}</div>
                </div>
              `;
            },
          },
          shadow: false,
          size: `100%`,
          slicedOffset: 0,
        },
        series: {
          animation: false,
          states: {
            inactive: {
              animation: false,
              enabled: true,
              opacity: 0.3,
            },
          },
          point: {
            events: {
              click: (event) => {
                return this.onPieChartClick?.(event.point);
              },
              mouseOut() {
                thisComponent.getContext(`segmentFilterController`).notifySeriesHovered(null);
              },
              mouseOver() {
                thisComponent.getContext(`segmentFilterController`).notifySeriesHovered({
                  seriesId: this.name,
                });
              },
            },
          },
        },
      },
      tooltip: {
        backgroundColor: null,
        padding: 0,
        borderRadius: 0,
        borderWidth: 0,
        formatter: this.createTooltipFormatter(),
        hideDelay: 0,
        shadow: false,
        useHTML: true,
      },
      series: [
        {
          type: `pie`,
          data: this.getChartData(),
          innerSize: `60%`,
        },
      ],
    };
  }

  private updateChart() {
    if (this.highcharts) {
      this.highcharts.update(
        this.getChartOptions(),
        true, // redraw after update
        true, // oneToOne: add/remove series when they change
      );
      this.highcharts.reflow();
    }
  }
}

export const PieChartEl = defineAndMakeJsxFactory<PieChartJsxProps>(`mp-pie-chart`, PieChart);
export const PieChartReact = panel2react<PieChartJsxProps>(`mp-pie-chart`, PieChart);
